import { ITimeoutService } from "angular";
import { Ng1StateDeclaration } from "@uirouter/angularjs";
import { filterStates } from "@gtmhub/assignees/assignees.states";
import { modal } from "@gtmhub/core/routing";
import { IGtmhubRootScopeService } from "@gtmhub/models";
import { attachNg1StateDependency, featureToggleEnabled } from "@gtmhub/resolve-guards";
import { ModuleFlag } from "@webapp/feature-toggles/models/feature-module.models";
import { FeatureFlag } from "@webapp/feature-toggles/models/feature-toggles.models";

class TasksStateFactory {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  public _tasks(base: string): Ng1StateDeclaration {
    return modal({
      name: `${base}._tasks`,
      view: "taskview",
      windowClass: "modal",
      abstract: true,
    });
  }

  public archived(base: string): Ng1StateDeclaration {
    return {
      name: `${base}._tasks.archived`,
      url: "archived/",
      data: {
        modal: null,
        requiresLogin: true,
        trackingPage: "Archived",
      },
      views: {
        "taskview@": {
          template: require("../tasks/views/archived.html"),
          controller: "ArchivedTasksCtrl",
        },
      },
    };
  }

  // eslint-disable-next-line @typescript-eslint/naming-convention
  public _task(base: string): Ng1StateDeclaration {
    return modal({
      name: `${base}._task`,
      data: {
        trackingPage: "Task View",
      },
      view: "taskview",
      windowClass: "sidebar-modal task-form-modal",
      abstract: true,
    });
  }

  public task(base: string): Ng1StateDeclaration {
    return {
      name: `${base}._task.task`,
      url: "task/:taskId/?parentId",
      data: {
        modal: null,
        requiresLogin: true,
        titleExpression: "task.name",
        trackingPage: "Task Details",
      },
      params: {
        parentType: null,
      },
      views: {
        "taskview@": {
          component: "taskForm",
        },
      },
    };
  }

  // eslint-disable-next-line @typescript-eslint/naming-convention
  public _createTask(base: string): Ng1StateDeclaration {
    return modal({
      name: `${base}._createTask`,
      data: {
        trackingPage: "Task View",
      },
      view: "createtask",
      windowClass: "medium-large create-form",
      backdropClass: "dark-backdrop",
      abstract: true,
    });
  }

  public createTaskForm(base: string): Ng1StateDeclaration {
    return {
      name: `${base}._createTask.createTask`,
      url: "create-task/",
      data: {
        requiresLogin: true,
        titleExpression: "task.name",
        trackingPage: "Task Details",
      },
      params: {
        parentType: "",
      },
      views: {
        "createtask@": {
          component: "createTaskForm",
        },
      },
    };
  }

  public tasks(base: string): Ng1StateDeclaration {
    return attachNg1StateDependency(
      {
        name: `${base}.tasks`,
        data: {
          requiresLogin: true,
          trackingPage: "Tasks",
        },
        url: "/tasks/?filterId",
        template: require("../tasks/views/tasks.html"),
        controller: "TasksCtrl",
      },
      featureToggleEnabled(ModuleFlag.Tasks)
    );
  }
}

class WorkflowStateFactory {
  public workflow(): Ng1StateDeclaration {
    return {
      name: "gtmhub.workflow",
      data: {
        requiresLogin: true,
        titleKey: "okrs_for_approval",
        trackingPage: "Home Page OKRs For Approval",
      },
      url: "/workflow/",
      template: require("../workflow/views/workflow.html"),
      controller: "WorkflowCtrl",
    };
  }
}

export const BASE_OKR_VIEW_STATE = "gtmhub.okrView";
export const BASE_OKR_VIEWS_STATE = "gtmhub.okrViews";

class OkrViewsStateFactory {
  public list(): Ng1StateDeclaration {
    return attachNg1StateDependency(
      {
        name: BASE_OKR_VIEWS_STATE,
        data: {
          requiresLogin: true,
          titleKey: "okr_views",
          trackingPage: "Okr Views Page",
        },
        params: {
          page: { dynamic: true },
        },
        url: "/okr-views/?page",
        template: require("../okrs/okr-views/components/okr-views-list/okr-views-list.component.html"),
        controller: "OkrViewsListCtrl",
        controllerAs: "$ctrl",
      },
      featureToggleEnabled(FeatureFlag.OkrViews)
    );
  }

  public singleView(): Ng1StateDeclaration {
    return attachNg1StateDependency(
      {
        name: BASE_OKR_VIEW_STATE,
        data: {
          requiresLogin: true,
          titleExpression: "$ctrl.okrView.name",
          trackingPage: "Okr View Page",
        },
        params: {
          okrViewId: null,
        },
        url: "/okr-views/:okrViewId/",
        template: require("../okrs/okr-views/components/okr-view/okr-view.component.html"),
        controller: "OkrViewCtrl",
        controllerAs: "$ctrl",
      },
      featureToggleEnabled(FeatureFlag.OkrViews)
    );
  }
}

class InboxStateFactory {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  public _message(base: string): Ng1StateDeclaration {
    return modal({
      name: `${base}._message`,
      view: "messageview",
      windowClass: "sidebar-modal",
      abstract: true,
    });
  }

  public thread(base: string): Ng1StateDeclaration {
    return {
      name: `${base}._message.thread`,
      url: "thread/:targetId/:targetType/?insightName",
      data: {
        trackingPage: "Thread Details",
      },
      views: {
        "messageview@": {
          template: require("../comments/views/thread.html"),
          controller: "ThreadCtrl",
        },
      },
    };
  }
}

class GoalsStateFactory {
  public goals(base: string): Ng1StateDeclaration {
    return {
      name: `${base}.goals`,
      data: {
        requiresLogin: true,
        titleExpression: "planningSession.title",
        trackingPage: "Session Page Opened",
      },
      params: {
        planningSessionId: null,
      },
      url: "/goals/:planningSessionId/",
      template: require("../goals/views/goals.html"),
      controller: "GoalsCtrl",
    };
  }

  public nested(): Ng1StateDeclaration {
    return {
      name: "gtmhub.goals.alignmentSidebar.nested",
      data: {
        requiresLogin: true,
        trackingPage: "OKRs Alignment Nested",
      },
      params: {
        id: null,
      },
      url: "nested/?id",
      views: {
        alignment: {
          template: require("./alignment-tree/states/alignment-nested.html"),
          controller: "AlignmentNestedCtrl",
        },
      },
      reloadOnSearch: false,
    };
  }

  public alignmentTree(base: string): Ng1StateDeclaration {
    return {
      name: `${base}.alignmentSidebar.tree`,
      data: {
        requiresLogin: true,
        trackingPage: "OKRs Alignment Tree",
      },
      url: "tree/",
      views: {
        alignmentTree: {
          template: require("./alignment-tree/states/alignment-tree.html"),
          controller: "AlignmentTreeCtrl",
        },
      },
      reloadOnSearch: false,
    };
  }

  public alignmentSidebar(base: string): Ng1StateDeclaration {
    return {
      name: `${base}.alignmentSidebar`,
      data: {
        requiresLogin: true,
        trackingPage: "OKRs Alignment Sidebar",
      },
      params: {
        locateId: null,
      },
      url: "alignment/?sharedId&sharedParentId&filterId",
      template: require("./alignment-tree/states/alignment-sidebar.html"),
      controller: "AlignmentSidebarCtrl",
      reloadOnSearch: false,
    };
  }

  public allGoals(base: string): Ng1StateDeclaration {
    return {
      name: `${base}.all`,
      data: {
        requiresLogin: true,
        trackingPage: "Session Grid View",
      },
      url: "all/?page&filterId",
      params: {
        filterId: { dynamic: true },
        page: { dynamic: true },
      },
      template: require("../goals/views/all.html"),
      controller: "AllGoalsCtrl",
    };
  }

  public goalDetails(base: string): Ng1StateDeclaration {
    return {
      name: `${base}.goal`,
      data: {
        titleExpression: "goal.name",
        trackingPage: "Goal Details",
      },
      params: {
        openingState: null,
      },
      // when navigating to goal view in OKR grid(from single team view, for example) query params cause controller to reload if not set to false
      reloadOnSearch: false,
      url: ":id/metrics/?sharedGoal",
      views: {
        "goaldetails@": {
          template: require("../goals/views/goal/goal.html"),
          controller: "GoalCtrl",
        },
      },
    };
  }

  // eslint-disable-next-line @typescript-eslint/naming-convention
  public _marketplace(base: string): Ng1StateDeclaration {
    return modal({
      name: `${base}._marketplace`,
      view: "marketplacemodalview",
      abstract: true,
      windowClass: "medium",
    });
  }

  public submit(base: string): Ng1StateDeclaration {
    return {
      name: `${base}._marketplace.submit`,
      data: {
        requiresLogin: true,
        trackingPage: "Submit Goal To Marketplace",
      },
      params: {
        goalsIds: null,
      },
      url: "submit-to-marketplace/:goalToBeSubmittedId/",
      views: {
        "marketplacemodalview@": {
          template: require("../goals/views/submit-to-marketplace.html"),
          controller: "SubmitToMarketplaceCtrl",
        },
      },
    };
  }

  // eslint-disable-next-line @typescript-eslint/naming-convention
  public _singleGoal(base: string): Ng1StateDeclaration {
    return modal({
      name: `${base}._goalSidebar`,
      view: "goaldetails",
      windowClass: "goal-sidebar auto-width",
      backdropClass: "dark-backdrop",
      abstract: true,
    });
  }

  // eslint-disable-next-line @typescript-eslint/naming-convention
  public _timeline(base: string): Ng1StateDeclaration {
    return modal({
      name: `${base}._timeline`,
      view: "timeline",
      windowClass: "sidebar-modal",
      abstract: true,
    });
  }

  public timeline(base: string): Ng1StateDeclaration {
    return {
      name: `${base}._timeline.timeline`,
      url: "timeline/",
      data: {
        trackingPage: "Goal Details Timeline",
      },
      views: {
        "timeline@": {
          template: require("../goals/views/goal/details/timeline.html"),
          controller: "GoalTimelineCtrl",
        },
      },
    };
  }

  // eslint-disable-next-line @typescript-eslint/naming-convention
  public _form(base: string): Ng1StateDeclaration {
    return modal({
      name: `${base}._goalForm`,
      view: "goalform",
      windowClass: "medium-large create-form create-goal",
      backdropClass: "dark-backdrop",
      abstract: true,
    });
  }

  public goalForm(base: string): Ng1StateDeclaration {
    const details: Ng1StateDeclaration = {
      template: require("../goals/views/form/form.html"),
      controller: "EditGoalCtrl",
    };

    return {
      name: `${base}._goalForm.form`,
      url: "form/?parentId&externalSessionId&teamId&parentType&flowName",
      // when navigating to goal form in OKR grid(from session conflicts modal, for example) query params cause controller to reload if not set to false
      // goal form post-init behavior does not depend on query/search params
      reloadOnSearch: false,
      params: {
        id: null,
        isModal: true,
        externalSessionId: null,
        teamId: null,
        flowName: null,
      },
      data: {
        trackingPage: "Create Objective Modal",
      },
      views: {
        "goalform@": details,
      },
    };
  }

  public bulk(base: string): Ng1StateDeclaration {
    return modal({
      name: `${base}.bulk`,
      view: "modalview",
      backdrop: true,
      windowClass: "sidebar-modal medium-small goals-bulk-actions",
      abstract: true,
      url: "bulk/",
    });
  }

  public assign(base: string): Ng1StateDeclaration {
    return {
      name: `${base}.bulk.assign`,
      url: "assign/",
      onEnter: [
        "$rootScope",
        "$timeout",
        ($rootScope: IGtmhubRootScopeService, $timeout: ITimeoutService) => {
          $timeout(() => ($rootScope.bulkAction = "assign")); // trigger on next digest cycle
        },
      ],
      onExit: [
        "$rootScope",
        ($rootScope: IGtmhubRootScopeService) => {
          $rootScope.bulkAction = null;
        },
      ],
      views: {
        "modalview@": {
          template: require("../goals/views/bulk-assign.html"),
          controller: "BulkAssignCtrl",
        },
      },
      params: {
        sessionSettings: null,
      },
    };
  }

  public tag(base: string): Ng1StateDeclaration {
    return {
      name: `${base}.bulk.tag`,
      onEnter: [
        "$rootScope",
        "$timeout",
        ($rootScope: IGtmhubRootScopeService, $timeout: ITimeoutService) => {
          $timeout(() => ($rootScope.bulkAction = "tag")); // trigger on next digest cycle
        },
      ],
      onExit: [
        "$rootScope",
        ($rootScope: IGtmhubRootScopeService) => {
          $rootScope.bulkAction = null;
        },
      ],
      url: "tag/",
      views: {
        "modalview@": {
          component: "bulkTag",
        },
      },
      params: {
        sessionId: null,
      },
    };
  }

  // eslint-disable-next-line @typescript-eslint/naming-convention
  public _createMetric(base: string): Ng1StateDeclaration {
    return modal({
      name: `${base}._createMetric`,
      view: "createmetric",
      windowClass: "medium-large create-form create-metric clear-modal-size-and-spacing",
      backdropClass: "dark-backdrop",
      abstract: true,
    });
  }

  public createMetricForm(base: string): Ng1StateDeclaration {
    return {
      name: `${base}._createMetric.createMetric`,
      url: "create-metric/?flowName",
      data: {
        requiresLogin: true,
        trackingPage: "Create Manual Key Result",
      },
      params: {
        id: null,
        isNewMetricAdded: null,
        flowName: null,
      },
      views: {
        "createmetric@": {
          template: require("./metrics/views/create-manual-metric.html"),
          controller: "MetricFormCtrl",
        },
      },
    };
  }

  // eslint-disable-next-line @typescript-eslint/naming-convention
  public _updateMetric(base: string): Ng1StateDeclaration {
    return modal({
      name: `${base}._updateMetric`,
      view: "updatemetric",
      windowClass: "medium modal-overflow-x-unset",
      backdropClass: "dark-backdrop",
      abstract: true,
    });
  }

  public updateMetricForm(base: string): Ng1StateDeclaration {
    return {
      name: `${base}._updateMetric.update`,
      url: "update-metric/:metricId/",
      params: {
        editOn: false,
      },
      data: {
        trackingPage: "Update Key Result",
      },
      views: {
        "updatemetric@": {
          template: require("./metrics/views/update-manual-metric.html"),
          controller: "UpdateMetricCtrl",
        },
      },
    };
  }

  public editMetricUpdate(base: string): Ng1StateDeclaration {
    return {
      name: `${base}._updateMetric.edit`,
      url: "edit-metric-update/:checkinId/",
      params: {
        editOn: true,
      },
      data: {
        trackingPage: "Edit Key Result Note",
      },
      views: {
        "updatemetric@": {
          template: require("./metrics/views/update-metric-note.html"),
          controller: "UpdateMetricCtrl",
        },
      },
    };
  }

  // eslint-disable-next-line @typescript-eslint/naming-convention
  public _metric(base: string): Ng1StateDeclaration {
    return modal({
      name: `${base}._metric`,
      view: "metric",
      windowClass: "sidebar-modal no-padding auto-width",
      backdropClass: "dark-backdrop",
      abstract: true,
    });
  }

  public metric(base: string): Ng1StateDeclaration {
    return {
      name: `${base}._metric.metric`,
      data: {
        titleExpression: "metric.name",
        trackingPage: "Key Result Details",
      },
      // when navigating to metric view in OKR grid(from insight usage, for example) query params cause controller to reload if not set to false
      reloadOnSearch: false,
      url: "metric/:metricId/?sharedMetric",
      views: {
        "metric@": {
          template: require("./metrics/views/metric.html"),
          controller: "MetricCtrl",
        },
      },
    };
  }

  // eslint-disable-next-line @typescript-eslint/naming-convention
  public _editMetric(base: string): Ng1StateDeclaration {
    return modal({
      name: `${base}._editMetric`,
      view: "editmetric",
      windowClass: "medium-large create-form create-metric clear-modal-size-and-spacing",
      backdropClass: "dark-backdrop",
      abstract: true,
    });
  }

  public editMetric(base: string): Ng1StateDeclaration {
    return {
      name: `${base}._editMetric.editMetric`,
      url: "edit-metric/:metricId/",
      data: {
        metricId: null,
        modal: null,
        requiresLogin: true,
        trackingPage: "Edit Key Result",
      },
      params: {
        isNewMetricAdded: null,
        id: null,
      },
      views: {
        "editmetric@": {
          template: require("./metrics/views/create-manual-metric.html"),
          controller: "MetricFormCtrl",
        },
      },
    };
  }

  public createDynamicMetric(base: string): Ng1StateDeclaration {
    return {
      name: `${base}._createMetric.createDynamicMetric`,
      url: "create-dynamic-metric/",
      data: {
        modal: null,
        requiresLogin: true,
        trackingPage: "Create Dynamic Key Result",
      },
      params: {
        metric: null,
      },
      views: {
        "createmetric@": {
          template: require("./metrics/views/edit-dynamic-metric.html"),
          controller: "EditDynamicMetricCtrl",
        },
      },
    };
  }

  public editDynamicMetric(base: string): Ng1StateDeclaration {
    return {
      name: `${base}._editMetric.editDynamicMetric`,
      url: "edit-dynamic-metric/:metricId/",
      data: {
        metricId: null,
        modal: null,
        requiresLogin: true,
        trackingPage: "Edit Dynamic Key Result",
      },
      params: {
        id: null,
        isNewMetricAdded: null,
      },
      views: {
        "editmetric@": {
          template: require("./metrics/views/edit-dynamic-metric.html"),
          controller: "EditDynamicMetricCtrl",
        },
      },
    };
  }

  // eslint-disable-next-line @typescript-eslint/naming-convention
  public _approve(base: string): Ng1StateDeclaration {
    return modal({
      name: `${base}._approve`,
      view: "approvemodal",
      abstract: true,
      windowClass: "medium",
    });
  }

  // eslint-disable-next-line @typescript-eslint/naming-convention
  public _review(base: string): Ng1StateDeclaration {
    return modal({
      name: `${base}._review`,
      view: "reviewmodal",
      abstract: true,
      windowClass: "medium-small",
    });
  }

  public sendForApproval(base: string): Ng1StateDeclaration {
    return {
      name: `${base}._approve.sendForApproval`,
      url: "send-for-approval/",
      data: {
        trackingPage: "Send Objective for Approval",
      },
      views: {
        "approvemodal@": {
          template: require("../goals/views/send-for-approval.html"),
          controller: "SendForApprovalCtrl",
        },
      },
    };
  }

  public review(base: string): Ng1StateDeclaration {
    return {
      name: `${base}._review.review`,
      url: "review/:action/",
      data: {
        trackingPage: "Objective Review",
      },
      views: {
        "reviewmodal@": {
          template: require("../goals/views/review.html"),
          controller: "ReviewCtrl",
        },
      },
    };
  }
}

class SessionsStateFactory {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  public _sessionSelector(base: string): Ng1StateDeclaration {
    return modal({
      name: `${base}._sessionSelector`,
      view: "sessionselector",
      abstract: true,
      windowClass: "sidebar-modal session-selector",
      url: "session-selector/",
      data: {
        trackingPage: "Session Selector",
      },
    });
  }

  // eslint-disable-next-line @typescript-eslint/naming-convention
  public _overview(): Ng1StateDeclaration {
    return modal({
      name: "gtmhub.sessions._overview",
      windowClass: "session-overview-modal",
      view: "sessionoverview",
      abstract: true,
      url: ":planningSessionId/",
      data: {
        trackingPage: "Session Overview",
      },
    });
  }

  public sessionSelector(base: string): Ng1StateDeclaration {
    return {
      name: `${base}._sessionSelector.sessionSelector`,
      data: {
        trackingPage: "Sessions Selector",
      },
      params: {},
      url: "sessions/",
      views: {
        "sessionselector@": {
          template: require("../sessions/views/selector.html"),
          controller: "SessionSelectorCtrl",
        },
      },
    };
  }

  public list(): Ng1StateDeclaration {
    return {
      name: "gtmhub.sessions",
      data: {
        requiresLogin: true,
        titleKey: "all_sessions",
        trackingPage: "All Sessions",
      },
      params: {},
      url: "/sessions/",
      template: require("../sessions/views/list.html"),
      controller: "PlanningSessionsCtrl",
    };
  }

  public overview(): Ng1StateDeclaration {
    return {
      name: "gtmhub.goals.overview",
      url: "overview/?tags",
      template: require("../sessions/views/overview.html"),
      controller: "PlanningSessionOverviewCtrl",
    };
  }

  public overviewModal(): Ng1StateDeclaration {
    return {
      name: "gtmhub.sessions._overview.overview",
      url: "overview/?tags",
      views: {
        "sessionoverview@": {
          template: require("../sessions/views/overview-modal.html"),
          controller: "PlanningSessionOverviewCtrl",
        },
      },
    };
  }

  public workflow(): Ng1StateDeclaration {
    return {
      name: "gtmhub.goals.workflow",
      data: {
        requiresLogin: true,
        trackingPage: "Session Approval Progress",
      },
      params: {},
      url: "workflow/",
      template: require("../sessions/views/workflow.html"),
      controller: "InspectWorkflowCtrl",
    };
  }

  // eslint-disable-next-line @typescript-eslint/naming-convention
  public _inspect(base: string): Ng1StateDeclaration {
    return modal({
      name: `${base}._inspect`,
      view: "inspect",
      abstract: true,
      data: {
        trackingPage: "Session Statistics Modal",
      },
      url: "inspect/",
      windowClass: "medium",
    });
  }

  public inspectList(base: string): Ng1StateDeclaration {
    return {
      name: `${base}._inspect.list`,
      url: ":statName/",
      params: {
        modal: null,
        requiresLogin: true,
      },
      data: {
        trackingPage: "Session Statistics Tab",
      },
      views: {
        "inspect@": {
          template: require("../sessions/views/inspect.html"),
          controller: "InspectStatisticsCtrl",
        },
      },
    };
  }

  public inspectGoals(): Ng1StateDeclaration {
    return {
      name: "gtmhub.goals.workflow._inspect.goals",
      url: "goals/:goalsStatName/",
      params: {
        requiresLogin: true,
        page: null,
      },
      data: {
        trackingPage: "Session Workflow Inspect Goals",
      },
      views: {
        "inspect@": {
          template: require("../sessions/views/goals-workflow.html"),
          controller: "InspectGoalsWorkflowCtrl",
        },
      },
    };
  }

  public inspectAssignees(): Ng1StateDeclaration {
    return {
      name: "gtmhub.goals.workflow._inspect.assignees",
      url: "assignees/:assigneesStatName/",
      params: {
        page: null,
        requiresLogin: true,
      },
      data: {
        trackingPage: "Session Workflow Inspect Assignees",
      },
      views: {
        "inspect@": {
          template: require("../sessions/views/assignees-workflow.html"),
          controller: "InspectAssigneesWorkflowCtrl",
        },
      },
    };
  }

  public nudge(): Ng1StateDeclaration {
    return {
      name: "gtmhub.goals.workflow._inspect.nudge",
      url: "nudge/:targetName/",
      params: {
        requiresLogin: true,
      },
      data: {
        trackingPage: "Nudge Users Modal",
      },
      views: {
        "inspect@": {
          template: require("../sessions/views/nudge-people.html"),
          controller: "NudgePeopleCtrl",
        },
      },
    };
  }

  public sessionModal(name: string): Ng1StateDeclaration {
    return modal({
      name,
      view: "sessionform",
      abstract: true,
      windowClass: "medium session-form",
      backdropClass: "dark-backdrop",
    });
  }

  public sessionForm(name: string): Ng1StateDeclaration {
    return {
      name,
      url: "form/:id?/",
      params: { isCloningOperation: false },
      data: {
        trackingPage: "Session Form",
      },
      views: {
        "sessionform@": {
          template: require("../sessions/components/session-form/session-form.html"),
          controller: "SessionFormCtrl",
          controllerAs: "$ctrl",
        },
      },
    };
  }

  // eslint-disable-next-line @typescript-eslint/naming-convention
  public _clone(): Ng1StateDeclaration {
    return modal({
      name: "gtmhub.sessions._clone",
      view: "sessionclone",
      data: {
        trackingPage: "Session Clone",
      },
      abstract: true,
      windowClass: "session-clone-modal medium",
      url: "clone/",
    });
  }

  public clone(): Ng1StateDeclaration {
    return {
      name: "gtmhub.sessions._clone.clone",
      data: {
        trackingPage: "Copy From Session by Id",
      },
      params: {
        originSessionId: null,
      },
      url: "clone-session/?originSessionId",
      views: {
        "sessionclone@": {
          template: require("../sessions/views/clone-session.html"),
          controller: "CloneSessionsCtrl",
        },
      },
    };
  }
}

const goalsStateFactory = new GoalsStateFactory();
const inboxStateFactory = new InboxStateFactory();
const sessionsStateFactory = new SessionsStateFactory();
const workflowStateFactory = new WorkflowStateFactory();
const okrViewsStateFactory = new OkrViewsStateFactory();
const tasksStateFactory = new TasksStateFactory();

export const createGoalStates = (base: string): Ng1StateDeclaration[] => [goalsStateFactory._form(base), goalsStateFactory.goalForm(base)];

export const createMetricStates = (base: string): Ng1StateDeclaration[] => [goalsStateFactory._createMetric(base), goalsStateFactory.createMetricForm(base)];

const editMetricStates = (base: string): Ng1StateDeclaration[] => [
  goalsStateFactory._editMetric(base),
  goalsStateFactory.editDynamicMetric(base),
  goalsStateFactory.editMetric(base),
];

export const updateMetricStates = (base: string): Ng1StateDeclaration[] => [goalsStateFactory._updateMetric(base), goalsStateFactory.updateMetricForm(base)];

export const taskStates = (base: string): Ng1StateDeclaration[] => [
  tasksStateFactory._task(base),
  tasksStateFactory.task(base),
  tasksStateFactory._createTask(base),
  tasksStateFactory.createTaskForm(base),
];

export const metricStates = (base: string): Ng1StateDeclaration[] => [
  goalsStateFactory._metric(base),
  goalsStateFactory.metric(base),
  ...updateMetricStates(`${base}._metric.metric`),
  goalsStateFactory.editMetricUpdate(`${base}._metric.metric`),
  inboxStateFactory._message(`${base}._metric.metric`),
  inboxStateFactory.thread(`${base}._metric.metric`),
  ...editMetricStates(`${base}._metric.metric`),
  ...taskStates(`${base}._metric.metric`),
  goalsStateFactory._updateMetric(`${base}._metric.metric._message.thread`),
  goalsStateFactory.editMetricUpdate(`${base}._metric.metric._message.thread`),
];

export const goalStates = (base: string): Ng1StateDeclaration[] => [
  goalsStateFactory._singleGoal(base),
  goalsStateFactory.goalDetails(`${base}._goalSidebar`),
  goalsStateFactory._marketplace(`${base}._goalSidebar.goal`),
  goalsStateFactory.submit(`${base}._goalSidebar.goal`),
  goalsStateFactory._timeline(`${base}._goalSidebar.goal`),
  goalsStateFactory.timeline(`${base}._goalSidebar.goal`),
  goalsStateFactory._approve(`${base}._goalSidebar.goal`),
  goalsStateFactory.sendForApproval(`${base}._goalSidebar.goal`),
  goalsStateFactory._review(`${base}._goalSidebar.goal`),
  goalsStateFactory.review(`${base}._goalSidebar.goal`),
  ...createGoalStates(`${base}._goalSidebar.goal`),
  ...createMetricStates(`${base}._goalSidebar.goal`),
  goalsStateFactory.createDynamicMetric(`${base}._goalSidebar.goal`),
  ...editMetricStates(`${base}._goalSidebar.goal`),
  ...updateMetricStates(`${base}._goalSidebar.goal`),
  ...metricStates(`${base}._goalSidebar.goal`),
  ...taskStates(`${base}._goalSidebar.goal`),
];

const alignmentTreeStates = (base: string): Ng1StateDeclaration[] => [
  goalsStateFactory.alignmentSidebar(base),
  goalsStateFactory.alignmentTree(base),
  ...goalStates(`${base}.alignmentSidebar.tree`),
  ...createGoalStates(`${base}.alignmentSidebar.tree`),
];

const goalsGridStates = (base: string): Ng1StateDeclaration[] => [
  goalsStateFactory.allGoals(base),
  goalsStateFactory.bulk(`${base}.all`),
  goalsStateFactory.assign(`${base}.all`),
  goalsStateFactory.tag(`${base}.all`),
  ...updateMetricStates(`${base}.all`),
  ...metricStates(`${base}.all`),
  ...goalStates(`${base}.all`),
  ...createGoalStates(`${base}.all`),
  sessionsStateFactory.sessionModal(`${base}.all.session`),
  sessionsStateFactory.sessionForm(`${base}.all.session.form`),
  sessionsStateFactory._sessionSelector(`${base}.all.session.form`),
  sessionsStateFactory.sessionSelector(`${base}.all.session.form`),
];

export const states: Ng1StateDeclaration[] = [
  // Goals & metrics
  goalsStateFactory.goals("gtmhub"),
  ...goalsGridStates("gtmhub.goals"),
  sessionsStateFactory.overview(),
  sessionsStateFactory._inspect("gtmhub.goals.overview"),
  sessionsStateFactory.inspectList("gtmhub.goals.overview"),
  ...createGoalStates("gtmhub.goals.overview"),
  sessionsStateFactory.workflow(),
  sessionsStateFactory._inspect("gtmhub.goals.workflow"),
  sessionsStateFactory.inspectGoals(),
  sessionsStateFactory.inspectAssignees(),
  sessionsStateFactory.nudge(),
  ...alignmentTreeStates("gtmhub.goals"),
  goalsStateFactory.nested(),
  ...goalStates("gtmhub.goals.alignmentSidebar.nested"),
  ...createGoalStates("gtmhub.goals.alignmentSidebar.nested"),
  ...createMetricStates("gtmhub.goals.alignmentSidebar.nested"),

  // Sessions
  sessionsStateFactory.list(),
  sessionsStateFactory._overview(),
  sessionsStateFactory.overviewModal(),
  sessionsStateFactory._inspect("gtmhub.sessions._overview.overview"),
  sessionsStateFactory.inspectList("gtmhub.sessions._overview.overview"),
  sessionsStateFactory.sessionModal("gtmhub.sessions._form"),
  sessionsStateFactory.sessionForm("gtmhub.sessions._form.form"),
  sessionsStateFactory._sessionSelector("gtmhub.sessions._form.form"),
  sessionsStateFactory.sessionSelector("gtmhub.sessions._form.form"),
  sessionsStateFactory._clone(),
  sessionsStateFactory.clone(),
  sessionsStateFactory.sessionModal("gtmhub.sessions._clone.clone.form"),
  sessionsStateFactory.sessionForm("gtmhub.sessions._clone.clone.form.create"),
  sessionsStateFactory._sessionSelector("gtmhub.sessions._clone.clone.form.create"),
  sessionsStateFactory.sessionSelector("gtmhub.sessions._clone.clone.form.create"),

  // Workflow
  workflowStateFactory.workflow(),
  ...goalStates("gtmhub.workflow"),

  // OKR Views
  okrViewsStateFactory.list(),
  okrViewsStateFactory.singleView(),
  ...alignmentTreeStates(BASE_OKR_VIEW_STATE),
  ...goalsGridStates(BASE_OKR_VIEW_STATE),

  // Tasks
  tasksStateFactory.tasks("gtmhub"),
  ...filterStates("gtmhub.tasks"),
  ...goalStates("gtmhub.tasks"),
  ...taskStates("gtmhub.tasks"),
  ...updateMetricStates("gtmhub.tasks"),
  ...metricStates("gtmhub.tasks"),
  tasksStateFactory._tasks("gtmhub.tasks"),
  tasksStateFactory.archived("gtmhub.tasks"),
];
